import {GetStaticProps, InferGetStaticPropsType} from 'next';
import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/router';
import React, {useMemo} from 'react';

import Col from '../../../../features/common/Col';
import {useEnvironment} from '../../../../features/common/EnvironmentProvider';
import Row from '../../../../features/common/Row';
import {TWENTY_FOUR_HOURS_IN_SECONDS} from '../../../../features/common/util';
import {Product} from '../../../../features/query/graphql';
import Button from '../../../../features/tool/components/buttons/Button';
import Map from '../../../../features/tool/components/Map';
import {getToolLayoutWithSupplier} from '../../../../features/tool/components/marketplace/LayoutWithSupplier';
import SupplierProducts from '../../../../features/tool/components/marketplace/SupplierProducts';
import ReviewList from '../../../../features/tool/components/ReviewsList';
import {ExtendedSupplier} from '../../../../features/tool/marketplace/types';
import {getExtendedSupplier, getSupplierProducts} from '../../../../features/tool/marketplace/util.server';
import {buildStaticPropsResponse} from '../../../../features/tool/util/dataFetch';
import URLS from '../../../../urls';
import {NextPageWithLayout} from '../../../_app';

type Props = {supplier: ExtendedSupplier; products: Product[]};

const SupplierPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({supplier, products}) => {
  const environment = useEnvironment();
  const router = useRouter();
  const isPreview = router.asPath.includes('/preview');
  const position =
    supplier.latitude !== null && supplier.longitude !== null
      ? {lat: supplier.latitude, lng: supplier.longitude}
      : undefined;

  const topReviews = useMemo(() => supplier.reviews?.slice(0, 3), [supplier.reviews]);

  return (
    <Col className="w-full mt-sm pb-lg overflow-y-auto [&>*]:shrink-0">
      <Head>
        <title key={'title'}>{supplier.name} - Envirocrops</title>
      </Head>
      <h2 className="text-2xl font-semibold">About</h2>
      <div className={'mt-xs text-sm whitespace-pre-line break-words'}>{supplier.about} </div>
      <h3 className="text-base font-semibold mt-md">Location</h3>
      <Row className="text-sm mt-xs mb-md">
        {supplier.address} · {supplier.town}
      </Row>
      <Map googleMapsApiKey={environment?.googleMaps.apiKey ?? ''} markerPosition={position} fixedMarker zoom={15} />
      {!!products.length && (
        <>
          <SupplierProducts title={'Products & Services'} products={products} className={'mt-md'} />
          <Button
            className="mt-md"
            variant="secondary"
            href={URLS.marketPlace.supplier(supplier.slug, isPreview).products()}
          >
            View more
          </Button>
        </>
      )}
      {topReviews && supplier.rating && (
        <>
          <ReviewList
            className="mt-md"
            title="Reviews"
            list={topReviews}
            count={supplier.rating.count}
            average={supplier.rating.average}
          />
          <div className={'mt-md text-xs text-gray-200'}>Showing most relevant reviews by</div>
          <div>
            <Image src="/img/google_on_white.png" alt="Google logo" width={59} height={18} />
          </div>
          <Button
            className="mt-md"
            variant="secondary"
            href={URLS.marketPlace.supplier(supplier.slug, isPreview).reviews}
          >
            Read more
          </Button>
        </>
      )}
    </Col>
  );
};

export const getStaticPaths = async () => ({paths: [], fallback: 'blocking'});

export const getStaticProps: GetStaticProps<Props> = async ({params}) => {
  if (typeof params?.id !== 'string') throw new Error('id is not a string');
  const supplier = await getExtendedSupplier({slug: params.id}, true);
  if (!supplier) return {notFound: true};
  const {products} = await getSupplierProducts(supplier.slug, 1, false, true);
  return buildStaticPropsResponse({supplier, products: products.slice(0, 3)}, TWENTY_FOUR_HOURS_IN_SECONDS);
};

export default SupplierPage;

SupplierPage.getLayout = getToolLayoutWithSupplier;

SupplierPage.title = 'Supplier';
